import * as voicifyApi from '../../../shared/api';
import IResult from '../../../shared/models/result/IResult';
import InteractionModel from '../../../shared/models/interactionModel/api/InteractionModel';
import InteractionModelUpdate from '../../../shared/models/interactionModel/api/InteractionModelUpdate';

export const SPANISH_GRACEFUL_HANGUP_WARNING_MESSAGE = '¿Hola? Solo quería asegurarme de que todavía sigues conmigo.';
export const SPANISH_GRACEFUL_HANGUP_FINAL_MESSAGE = 'Parece que no te estoy escuchando, así que puede que haya un problema con la conexión. Como no pudimos terminar, es posible que tengas que empezar de nuevo cuando vuelvas a llamar, y con gusto te ayudaré. Voy a colgar ahora. ¡Cuídate!';

// Match the editor's explicit save without adding whitespace or changing intent behavior.
export const createSpanishInteractionModelSave = (model: InteractionModel): InteractionModelUpdate | undefined => {
    if (model?.locale !== 'es-US' || !Array.isArray(model.intents)) return undefined;
    const editableIntents = model.intents.filter(intent => intent && intent.isUserEditable !== false
        && (intent.isCustom ? intent.id : intent.displayName));
    const intents = editableIntents.reduce((updates, intent) => {
        if (typeof intent.enhancedMatchingDescription !== 'string'
            && typeof intent.requiresExactUtteranceMatch !== 'boolean') return updates;
        updates[intent.isCustom ? intent.id : intent.displayName] = {
            addedUtterances: [],
            removedUtterances: [],
            addedSlots: [],
            removedSlots: [],
            updatedDisplayName: '',
            requiresExactUtteranceMatch: intent.requiresExactUtteranceMatch,
            allowEnhancedIntentMatching: intent.allowEnhancedIntentMatching,
            enhancedMatchingDescription: intent.enhancedMatchingDescription,
            alwaysIncludeInPrompt: intent.alwaysIncludeInPrompt,
            disabledForFeatureFlagIds: intent.disabledForFeatureFlagIds
        };
        return updates;
    }, {} as InteractionModelUpdate['intents']);
    if (!Object.keys(intents).length) return undefined;
    return {
        locale: 'es-US',
        intents,
        disableIntents: [],
        enableIntents: [],
        removeCustomIntents: [],
        addCustomIntents: []
    };
};

const saveSyncedSpanishInteractionModel = async (applicationId: string, onWarning: (warning: string) => void) => {
    const modelResult = await runSpanishSetupStep('interaction model load', () =>
        voicifyApi.getInteractionModelByLocale(applicationId, 'es-US'), onWarning);
    if (!modelResult) return;
    const update = createSpanishInteractionModelSave(modelResult.data);
    if (!update) {
        onWarning('Spanish interaction model could not be saved because no editable Spanish intent was returned. You can continue, but Spanish setup may be incomplete.');
        return;
    }
    await runSpanishSetupStep('interaction model save', () =>
        voicifyApi.commitUpdateToInteractionModel(applicationId, 'es-US', update), onWarning);
};

export interface SpanishLineWarningProps {
    onSpanishSetupWarning: (warning: string) => void;
    clearSpanishSetupWarnings: () => void;
}

export const spanishSetupWarning = (step: string): string =>
    `Spanish ${step} failed. You can continue, but Spanish setup may be incomplete.`;

// These steps are best effort: a failed result must be visible without stopping setup.
export const runSpanishSetupStep = async <T>(
    step: string,
    action: () => Promise<IResult<T>>,
    onWarning: (warning: string) => void
): Promise<IResult<T> | undefined> => {
    let result: IResult<T>;
    try {
        result = await action();
        if (result?.resultType === 'Ok' && (result.data as unknown) !== false) {
            return result;
        }
    } catch {
        // API wrappers usually return errors, but callers can also reject.
    }
    const diagnostics = [
        result?.httpStatus ? 'HTTP ' + result.httpStatus : '',
        result?.requestId ? 'request ID ' + result.requestId : ''
    ].filter(Boolean).join('; ');
    onWarning(spanishSetupWarning(step) + (diagnostics ? ' (' + diagnostics + ')' : ''));
    return undefined;
};

export const setupSpanishInteractionModel = async (applicationId: string, onWarning: (warning: string) => void) => {
    const languagesResult = await runSpanishSetupStep('language lookup', () => voicifyApi.getAllLanguages(), onWarning);
    const languages = Array.isArray(languagesResult?.data) ? languagesResult.data : [];
    const enUsLanguage = languages.find(language => language.shortCode === 'en-US');
    const esUsLanguage = languages.find(language => language.shortCode === 'es-US');

    if (enUsLanguage?.id && esUsLanguage?.id) {
        await runSpanishSetupStep('language update', () =>
            voicifyApi.updateApplicationLanguages(applicationId, [enUsLanguage.id, esUsLanguage.id]), onWarning);
        const syncResult = await runSpanishSetupStep('interaction model sync', () =>
            voicifyApi.syncNLPAndInteractionModel(applicationId, [esUsLanguage.id], enUsLanguage.id), onWarning);
        if (syncResult) {
            await saveSyncedSpanishInteractionModel(applicationId, onWarning);
        }
    } else if (languagesResult) {
        onWarning('Spanish language lookup could not find en-US or es-US language IDs. You can continue, but Spanish setup may be incomplete.');
    }

    // Queueing and phone configuration do not require the language IDs.
    await runSpanishSetupStep('interaction model deployment queue', () => voicifyApi.queueDeployments(applicationId), onWarning);
};
